import { createFileRoute, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { BrandThemeProvider } from "@/components/brand-theme-provider";
import { Button } from "@/components/ui/button";
import { UserCog, LogOut } from "lucide-react";
import { ACTIVE_STAFF_KEY, ACTIVE_STAFF_NAME_KEY } from "@/lib/active-staff";
import { NotificationBell } from "@/components/parent/NotificationBell";
import { PendingEnrollmentGate } from "@/components/shared/PendingEnrollmentGate";
import { TrainingCenterSetupGate } from "@/components/shared/TrainingCenterSetupGate";

import { clearSupabaseAuthStorage, isStorageBlocked } from "@/lib/browser-storage";
import { getStaffAccessToken } from "@/lib/staff-access-link";
import {
  StaffLogoutNoticeDialog,
  useStaffLogoutFlow,
} from "@/components/shared/StaffLogoutNotice";

// Rotas que NÃO exigem perfil ativo (são parte do fluxo de seleção)
const PROFILE_FLOW_PATHS = new Set(["/bem-vindo", "/selecionar-perfil"]);
// Prefixos permitidos para responsáveis (parent_student)
const PARENT_PATH_PREFIXES = ["/responsavel", "/selecionar-ct"];
const PARENT_DASHBOARD_PATH = "/dashboard";

function isParentPath(pathname: string) {
  return PARENT_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function isAllowedParentPath(pathname: string) {
  // O Menu Principal (/dashboard) também é do responsável: mostra os botões de acesso.
  return pathname === PARENT_DASHBOARD_PATH || pathname === "/dashboard" || isParentPath(pathname);
}


function isParentRole(role: string | undefined) {
  return role === "parent_student" || role === "parent" || role === "usuario" || role === "user";
}

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      // Sessão inválida/corrompida neste navegador: limpar antes de mandar ao login,
      // senão o token velho continua sendo reenviado e todas as telas ficam vazias.
      clearSupabaseAuthStorage();
      // Colaborador que entrou por link de acesso volta para a tela do PIN,
      // não para o login por e-mail/senha (ao qual ele não tem acesso).
      const staffToken = getStaffAccessToken();
      if (staffToken) throw redirect({ to: "/acesso/$token", params: { token: staffToken } });
      throw redirect({ to: "/auth" });
    }
    let role = (data.user.user_metadata as any)?.role as string | undefined;
    if (!role || !isParentRole(role)) {
      const { data: prof } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", data.user.id)
        .maybeSingle();
      if (prof?.role) role = prof.role as string;
    }
    // Administrador geral do sistema: painel próprio em /sa.
    if (role === "system_admin" && !location.pathname.startsWith("/sa")) {
      throw redirect({ to: "/sa" });
    }
    // Responsáveis usam o mesmo layout do app, mas apenas nas rotas próprias.
    if (isParentRole(role) && !isAllowedParentPath(location.pathname)) {
      throw redirect({ to: PARENT_DASHBOARD_PATH });
    }
    return { user: data.user, role };

  },

  component: AuthenticatedLayout,
});

// No celular, o responsável entra com o menu lateral já aberto (uma vez por sessão).
function MobileMenuAutoOpen() {
  const { isMobile, setOpenMobile } = useSidebar();
  useEffect(() => {
    if (!isMobile) return;
    try {
      if (sessionStorage.getItem("parent_menu_auto_open") === "1") return;
      sessionStorage.setItem("parent_menu_auto_open", "1");
    } catch { /* storage bloqueado */ }
    setOpenMobile(true);
  }, [isMobile, setOpenMobile]);
  return null;
}

function AuthenticatedLayout() {

  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { role } = Route.useRouteContext();
  const isParent = isParentRole(role);
  const isSystemAdmin = role === "system_admin";

  const [activeStaffName, setActiveStaffName] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [storageBlocked, setStorageBlocked] = useState(false);

  const logoutFlow = useStaffLogoutFlow();
  const signOut = logoutFlow.requestSignOut;



  useEffect(() => {
    setStorageBlocked(isStorageBlocked());
  }, []);

  useEffect(() => {
    // Administrador geral: não passa pelo fluxo de perfis/PIN do Centro.
    if (isSystemAdmin) {
      setReady(true);
      return;
    }
    // Responsáveis: pular fluxo de staff, mas manter layout completo com menu.
    if (isParent) {
      if (!isAllowedParentPath(pathname)) navigate({ to: PARENT_DASHBOARD_PATH, replace: true });
      setReady(true);
      return;
    }

    let id: string | null = null;
    let name: string | null = null;
    try {
      id = localStorage.getItem(ACTIVE_STAFF_KEY);
      name = localStorage.getItem(ACTIVE_STAFF_NAME_KEY);
    } catch { /* storage bloqueado */ }
    setActiveStaffName(name);
    if (!id && !PROFILE_FLOW_PATHS.has(pathname)) {
      navigate({ to: "/bem-vindo", replace: true });
      return;
    }
    setReady(true);
  }, [pathname, navigate, isParent, isSystemAdmin]);

  if (!ready) return null;

  // Painel do administrador geral traz o próprio layout/menu.
  if (isSystemAdmin) return <Outlet />;



  // Telas do fluxo de seleção: renderizar sem sidebar
  if (PROFILE_FLOW_PATHS.has(pathname)) {
    return (
      <div className="min-h-screen bg-muted/40">
        <Outlet />
      </div>
    );
  }


  const switchProfile = () => {
    localStorage.removeItem(ACTIVE_STAFF_KEY);
    localStorage.removeItem(ACTIVE_STAFF_NAME_KEY);
    navigate({ to: "/selecionar-perfil" });
  };

  return (
    <SidebarProvider>
      <BrandThemeProvider>
      {isParent && <MobileMenuAutoOpen />}
      {isParent && pathname.startsWith("/responsavel") && <PendingEnrollmentGate />}
      <div className="min-h-screen flex w-full">

        <AppSidebar role={role} />

        <div className="flex-1 flex flex-col">
          <header className="h-14 flex items-center border-b px-4 gap-2">
            <SidebarTrigger />
            <span className="text-sm font-medium text-muted-foreground">Menu</span>
            <div className="ml-auto flex items-center gap-2">
              {isParent && <NotificationBell />}
              {!isParent && activeStaffName ? (
                <span className="text-sm text-muted-foreground hidden sm:inline">{activeStaffName}</span>
              ) : null}
              {!isParent && (
                <Button variant="ghost" size="sm" onClick={switchProfile}>
                  <UserCog className="h-4 w-4 mr-2" /> Trocar perfil
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={signOut}>
                <LogOut className="h-4 w-4 mr-2" /> {isParent ? "Sair" : "Sair do Sistema"}
              </Button>
            </div>
          </header>
          <main className="flex-1 p-6">
            {storageBlocked && (
              <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                Seu navegador está bloqueando o armazenamento local deste site (extensão de
                privacidade ou proteção contra rastreamento). Libere o site nas configurações do
                navegador/extensão para que as telas carreguem normalmente.
              </div>
            )}
            <TrainingCenterSetupGate bypass={isParent || pathname === "/m/dados-ct"}>
              <Outlet />
            </TrainingCenterSetupGate>

          </main>
        </div>
      </div>
      <StaffLogoutNoticeDialog
        open={logoutFlow.open}
        onOpenChange={logoutFlow.setOpen}
        onConfirm={logoutFlow.confirmSignOut}
      />
      </BrandThemeProvider>
    </SidebarProvider>
  );
}
